import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { UzButton, UzConfirmDialog, UzStatusBadge } from '@utazzo/ui';
import { ArrowLeft, Check, CircleAlert, Eye, Loader2, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { MEDIA_FOLDER, MediaFolderProvider } from '../components/media-folder';
import { useCan } from '../lib';

import { AddSectionDrawer } from './components/AddSectionDrawer';
import { FixedTabsEditor } from './components/FixedTabsEditor';
import { SeoPanel } from './components/SeoPanel';
import { SortableSectionCard } from './components/SortableSectionCard';
import { CUSTOM_PAGES_CONFIG, type PageScreenConfig } from './page-screen-config';
import { type SectionType, sectionLabel } from './section-presets';
import { createSection, deleteSection } from './sections.api';
import { EnquiriesTab } from './tabs/EnquiriesTab';
import { PositionsTab } from './tabs/PositionsTab';
import { useEditorSave } from './use-editor-save';
import { usePage, usePublishPage, useUnpublishPage } from './use-pages';

import type { PageSection } from './pages.api';
import type { UpdateWebsitePageRequest, WebsitePageDetail } from '@utazzo/shared';

import { PageShell } from '@/components/page-shell';
import { useListReturn } from '@/hooks/use-list-return';

const STATUS_BADGE: Record<string, { status: 'success' | 'draft' | 'inactive'; label: string }> = {
  PUBLISHED: { status: 'success', label: 'Published' },
  DRAFT: { status: 'draft', label: 'Draft' },
  ARCHIVED: { status: 'inactive', label: 'Archived' },
};

type EditorTab = 'sections' | 'positions' | 'enquiries' | 'seo';

const EDITOR_TABS: readonly EditorTab[] = ['sections', 'positions', 'enquiries', 'seo'];

/** Guards the `?tab=` URL value so a hand-typed one can't break the editor. */
const isEditorTab = (v: string | null): v is EditorTab =>
  v !== null && (EDITOR_TABS as readonly string[]).includes(v);

type PositionItem = Record<string, unknown>;

/**
 * Ensure every OPEN_POSITIONS item has a stable `id`. Legacy positions
 * authored before the Position tab have none; we backfill in local state (no
 * queue/dirty) so the table can key/edit rows. Ids persist once the user saves.
 */
const ensurePositionIds = (s: PageSection): PageSection => {
  if (s.sectionType !== 'OPEN_POSITIONS') return s;
  const data = (s.data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(data.items) ? (data.items as PositionItem[]) : [];
  if (items.every((it) => typeof it.id === 'string' && it.id)) return s;
  return {
    ...s,
    data: {
      ...data,
      items: items.map((it) =>
        typeof it.id === 'string' && it.id ? it : { ...it, id: crypto.randomUUID() },
      ),
    },
  };
};

const SaveStatus = ({
  state,
  dirty,
}: {
  state: 'idle' | 'saving' | 'saved' | 'error';
  dirty: boolean;
}): React.JSX.Element | null => {
  if (state === 'saving') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-(--fg-faint)">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-(--ds-error)">
        <CircleAlert className="h-3.5 w-3.5" /> Save failed
      </span>
    );
  }
  if (dirty) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-(--fg-muted)">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-(--fg-faint)">
        <Check className="h-3.5 w-3.5 text-(--ds-success)" /> Saved
      </span>
    );
  }
  return null;
};

/**
 * @param pageId When provided, edits this page directly (used by the Career
 *   entry, which renders the editor at `/career` so that sidebar item stays
 *   selected). Otherwise the id comes from the `/pages/:id` route param.
 * @param config Page-type surface (endpoint + permission caps + editor route).
 *   Defaults to Custom Pages; the router passes the Tourism Boards / Landing
 *   Pages config so load/save hit the type-scoped, separately-gated endpoint
 *   and "Back" returns to the right list.
 */
export const PageEditor = ({
  pageId,
  config = CUSTOM_PAGES_CONFIG,
}: {
  pageId?: string;
  config?: PageScreenConfig;
} = {}): React.JSX.Element => {
  const { id: routeId } = useParams<{ id: string }>();
  const id = pageId ?? routeId;
  const backToList = useListReturn(config.backPath ?? config.editorBase);
  const qc = useQueryClient();
  const canWrite = useCan(config.caps.write);
  const canPublish = useCan(config.caps.publish);
  // Structure = the Sections + SEO tabs and "Add section". Only surfaces that
  // declare `structureCap` restrict it; everywhere else this stays true.
  const canEditStructure = useCan(config.structureCap ?? config.caps.write);
  const showStructureTabs = config.structureCap === undefined || canEditStructure;

  const pageQuery = usePage(id ?? null, config.basePath);
  const publish = usePublishPage(config.basePath);
  const unpublish = useUnpublishPage(config.basePath);

  const [sections, setSections] = useState<PageSection[]>([]);
  const [meta, setMeta] = useState<WebsitePageDetail | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  // Tab lives in the URL so it can be linked to - the HR dashboard tiles open
  // `/career?tab=enquiries&f.status=RECEIVED` and land on the filtered table
  // (`f.*` is the grid's own filter state, see use-data-grid-state).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: EditorTab = isEditorTab(tabParam) ? tabParam : 'sections';
  const setTab = (next: EditorTab): void => {
    // Read the LIVE query string, not React Router's snapshot: the enquiries
    // grid writes its `f.*` filters with a raw history.replaceState that the
    // router never observes, so rebuilding from `searchParams` here would drop
    // every filter the user had set (see use-data-grid-state).
    const params = new URLSearchParams(window.location.search);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const [confirmDelete, setConfirmDelete] = useState<PageSection | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (pageQuery.data) {
      setSections(pageQuery.data.sections.map(ensurePositionIds));
      setMeta(pageQuery.data);
    }
  }, [pageQuery.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const page = pageQuery.data;

  // Explicit save: edits queue locally; the header Save button persists them.
  const { dirty, saveState, queueSection, queueMeta, queueOrder, save } = useEditorSave(
    id ?? null,
    config.basePath,
  );

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['website-pages'] });
    void pageQuery.refetch();
  };

  const onDragEnd = (e: DragEndEvent): void => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(sections, oldIndex, newIndex);
    setSections(next);
    queueOrder(next.map((s) => s.id));
  };

  const toggleVisible = (s: PageSection): void => {
    const updated = { ...s, isVisible: !s.isVisible };
    setSections((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    queueSection(updated);
  };

  const saveTitle = (s: PageSection, title: string | null): void => {
    const updated = { ...s, title };
    setSections((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    queueSection(updated);
  };

  const saveSubtitle = (s: PageSection, subtitle: string | null): void => {
    const updated = { ...s, subtitle };
    setSections((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    queueSection(updated);
  };

  const setSectionData = (sec: PageSection, nextData: Record<string, unknown>): void => {
    const updated = { ...sec, data: nextData };
    setSections((prev) => prev.map((x) => (x.id === sec.id ? updated : x)));
    queueSection(updated);
  };

  // Positions live inside the OPEN_POSITIONS section's items array. The Position
  // tab edits them as a table + modal; writes go through the same queue/save.
  const positionsSection = sections.find((s) => s.sectionType === 'OPEN_POSITIONS');
  const positionsData = (positionsSection?.data ?? {}) as Record<string, unknown>;
  const positions: PositionItem[] = Array.isArray(positionsData.items)
    ? (positionsData.items as PositionItem[])
    : [];
  const hasPositions = positionsSection !== undefined;
  const writePositions = (next: PositionItem[]): void => {
    if (!positionsSection) return;
    setSectionData(positionsSection, { ...positionsData, items: next });
  };

  // Tab set for the free-form editor. Sections + SEO drop out for a caller
  // that can't edit page structure (see `structureCap`), leaving the content
  // tabs. Derive the active tab rather than syncing state so a restricted user
  // never renders a tab they can't see - `tab` stays the last valid choice.
  const availableTabs: readonly EditorTab[] = [
    ...(showStructureTabs ? (['sections'] as const) : []),
    ...(hasPositions ? (['positions', 'enquiries'] as const) : []),
    ...(showStructureTabs ? (['seo'] as const) : []),
  ];
  // A structure-restricted caller on a page with no OPEN_POSITIONS section
  // would otherwise be left with nothing; the applicant queue is independent
  // of that section, so fall back to it.
  const visibleTabs: readonly [EditorTab, ...EditorTab[]] =
    availableTabs.length > 0 ? (availableTabs as [EditorTab, ...EditorTab[]]) : ['enquiries'];
  const activeTab: EditorTab = visibleTabs.includes(tab) ? tab : visibleTabs[0];

  // SEO: optimistic local merge + queue for the batched flush.
  const saveSeo = (payload: Partial<UpdateWebsitePageRequest>): void => {
    setMeta((m) => (m ? { ...m, ...payload } : m));
    queueMeta(payload);
  };

  // Add/remove are structural (need a server id / removal): persist pending
  // edits first so the subsequent refetch doesn't drop them, then refresh.
  const addSection = async (type: SectionType): Promise<void> => {
    if (!id) return;
    setAdding(true);
    try {
      await save();
      // Omit sortOrder: the server appends new sections at the end (max + 1),
      // which is robust even when existing sortOrder values have gaps/ties.
      const created = await createSection({
        pageId: id,
        sectionType: type,
        dataJson: {},
      });
      setDrawerOpen(false);
      setOpenId(created.id);
      refresh();
    } finally {
      setAdding(false);
    }
  };

  const addTemplate = async (types: SectionType[]): Promise<void> => {
    if (!id) return;
    setAdding(true);
    try {
      await save();
      for (const t of types) {
        // Sequential so each append lands after the previous (server assigns
        // sortOrder = max + 1 per create). Presets are small (≤4).
        await createSection({ pageId: id, sectionType: t, dataJson: {} });
      }
      setDrawerOpen(false);
      refresh();
    } finally {
      setAdding(false);
    }
  };

  if (!id) {
    return (
      <PageShell title="New page" description="Creating pages from the editor is coming next.">
        <UzButton variant="ghost" onClick={backToList}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to pages
        </UzButton>
      </PageShell>
    );
  }

  if (pageQuery.isLoading) {
    return (
      <PageShell title="Loading…" description="">
        {null}
      </PageShell>
    );
  }
  if (pageQuery.isError || !page) {
    return (
      <PageShell title="Page not found" description="This page may have been deleted.">
        <UzButton variant="ghost" onClick={backToList}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to pages
        </UzButton>
      </PageShell>
    );
  }

  const badge = STATUS_BADGE[page.status] ?? { status: 'draft' as const, label: page.status };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      {/* Editor header. Phones: title row on top at full width, the save /
          publish controls wrap onto their own row underneath. */}
      <div className="flex items-center justify-between border-b border-(--border) bg-(--bg) px-5 py-3 max-sm:flex-wrap max-sm:gap-2 max-sm:px-3">
        <div className="flex items-center gap-3 max-sm:w-full max-sm:min-w-0 max-sm:gap-2">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => {
              if (dirty && !window.confirm('You have unsaved changes. Leave without saving?'))
                return;
              backToList();
            }}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </UzButton>
          <div className="max-sm:min-w-0 max-sm:flex-1">
            <div className="flex items-center gap-2 max-sm:flex-wrap max-sm:gap-x-2 max-sm:gap-y-0.5">
              <h1 className="font-heading text-lg font-semibold text-(--fg)">{page.title}</h1>
              <UzStatusBadge status={badge.status} label={badge.label} />
            </div>
            <span className="text-xs text-(--fg-faint) max-sm:break-all">
              {config.publicPathPrefix ?? ''}/{page.slug}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 max-sm:w-full max-sm:flex-wrap max-sm:justify-end max-sm:gap-2">
          <SaveStatus state={saveState} dirty={dirty} />
          {canWrite && (
            <UzButton
              size="sm"
              onClick={() => void save()}
              loading={saveState === 'saving'}
              disabled={!dirty || saveState === 'saving'}
            >
              Save
            </UzButton>
          )}
          {canPublish && (
            <>
              {page.status === 'PUBLISHED' ? (
                <UzButton
                  variant="secondary"
                  size="sm"
                  onClick={() => unpublish.mutateAsync(page.id).then(refresh)}
                  loading={unpublish.isPending}
                >
                  Unpublish
                </UzButton>
              ) : (
                <UzButton
                  variant="outline"
                  size="sm"
                  onClick={() => publish.mutateAsync(page.id).then(refresh)}
                  loading={publish.isPending}
                >
                  <Eye className="mr-2 h-4 w-4" /> Publish
                </UzButton>
              )}
            </>
          )}
        </div>
      </div>

      {/* Single-pane editor. Media uploaded from here lands under custom-pages/. */}
      <MediaFolderProvider folder={MEDIA_FOLDER.customPages}>
        <div className="flex-1 overflow-y-auto">
          <div
            className={`mx-auto w-full ${
              config.fixedTabs
                ? 'max-w-5xl px-2 py-2'
                : (config.contentMaxWidthClass ?? 'max-w-3xl')
            }`}
          >
            {config.fixedTabs ? (
              <FixedTabsEditor
                tabs={config.fixedTabs}
                groups={config.tabGroups}
                sections={sections}
                meta={meta}
                canWrite={canWrite}
                adding={adding}
                onAdd={(st) => void addSection(st as SectionType)}
                onSaveTitle={saveTitle}
                onSaveSubtitle={saveSubtitle}
                onChangeData={setSectionData}
                onSaveSeo={saveSeo}
                publicPathPrefix={config.publicPathPrefix}
              />
            ) : (
              <>
                {/* Phones: the tab pills scroll sideways (never squeezed onto two
                    lines) and "Add section" drops below when it can't fit. */}
                <div className="flex items-center justify-between gap-2 border-b border-(--border) px-4 py-2.5 max-sm:flex-wrap max-sm:px-3">
                  <div className="flex rounded-md bg-(--z-100) p-0.5 max-sm:max-w-full max-sm:overflow-x-auto">
                    {visibleTabs.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors max-sm:shrink-0 max-sm:whitespace-nowrap ${
                          activeTab === t
                            ? 'bg-(--bg) text-(--fg) shadow-sm'
                            : 'text-(--fg-faint) hover:text-(--fg)'
                        }`}
                      >
                        {t === 'sections'
                          ? `Sections (${sections.length})`
                          : t === 'positions'
                            ? `Position (${positions.length})`
                            : t === 'enquiries'
                              ? 'Enquiries'
                              : 'SEO'}
                      </button>
                    ))}
                  </div>
                  {activeTab === 'sections' && canWrite && (
                    <UzButton
                      size="sm"
                      variant="secondary"
                      loading={adding}
                      onClick={() => setDrawerOpen(true)}
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Add section
                    </UzButton>
                  )}
                </div>

                {activeTab === 'positions' ? (
                  <PositionsTab
                    positions={positions}
                    canWrite={canWrite}
                    onChange={writePositions}
                  />
                ) : activeTab === 'enquiries' ? (
                  <EnquiriesTab canWrite={canWrite} />
                ) : activeTab === 'seo' ? (
                  meta && (
                    <SeoPanel
                      page={meta}
                      onSave={saveSeo}
                      publicPathPrefix={config.publicPathPrefix}
                      disabled={!canWrite}
                    />
                  )
                ) : (
                  <div className="flex flex-col gap-2 px-4 py-4">
                    {sections.length === 0 && (
                      <p className="rounded-md border border-dashed border-(--border-strong) p-6 text-center text-sm text-(--fg-faint)">
                        No sections yet. Use “Add section” to compose this page.
                      </p>
                    )}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={onDragEnd}
                    >
                      <SortableContext
                        items={sections.map((s) => s.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="flex flex-col gap-2">
                          {sections.map((s, i) => (
                            <SortableSectionCard
                              key={s.id}
                              section={s}
                              index={i}
                              open={openId === s.id}
                              canWrite={canWrite}
                              onToggleOpen={() => setOpenId(openId === s.id ? null : s.id)}
                              onSaveTitle={(title) => saveTitle(s, title)}
                              onSaveSubtitle={(subtitle) => saveSubtitle(s, subtitle)}
                              onChangeData={(next) => setSectionData(s, next)}
                              onToggleVisible={() => toggleVisible(s)}
                              onDelete={() => setConfirmDelete(s)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                    {canWrite && sections.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setDrawerOpen(true)}
                        className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-(--border-strong) py-2.5 text-sm text-(--fg-muted) transition-colors hover:border-(--primary) hover:text-(--fg)"
                      >
                        <Plus className="h-4 w-4" /> Add section
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </MediaFolderProvider>

      <AddSectionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onAddSection={(t) => void addSection(t)}
        onAddTemplate={(types) => void addTemplate(types)}
      />

      <UzConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Remove this section?"
        description={
          confirmDelete
            ? `The ${sectionLabel(confirmDelete.sectionType)} section will be removed from this page.`
            : ''
        }
        confirmLabel="Remove"
        tone="destructive"
        onConfirm={async () => {
          if (confirmDelete) {
            await save();
            await deleteSection(confirmDelete.id);
            setConfirmDelete(null);
            refresh();
          }
        }}
      />
    </div>
  );
};
